import React, { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { MapPin, HardHat, ChevronRight } from 'lucide-react';
import { useTenant } from '../context/TenantContext';

// Slug du tenant historique : sa vitrine en dur est conservée à l'identique.
const LEGACY_DNF_SLUG = 'dnf-sas';

const BRAND = '#8B4513';
const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');

const STATUS_LABEL = {
  NOT_STARTED: 'Non démarré',
  IN_PROGRESS: 'En cours',
  ON_HOLD: 'En pause',
  COMPLETED: 'Terminé',
};
const STATUS_COLORS = {
  NOT_STARTED: { bg: '#f1f5f9', text: '#475569' },
  IN_PROGRESS: { bg: '#dbeafe', text: '#1d4ed8' },
  ON_HOLD:     { bg: '#fef3c7', text: '#92400e' },
  COMPLETED:   { bg: '#dcfce7', text: '#166534' },
};

function ChantierCard({ p }) {
  const sc = STATUS_COLORS[p.status] || STATUS_COLORS.IN_PROGRESS;
  return (
    <Link to={`/chantier/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div
        style={{
          background: 'white', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 2px 16px rgba(139,69,19,0.08)',
          border: '1px solid rgba(139,69,19,0.08)',
          transition: 'transform 0.15s, box-shadow 0.15s', cursor: 'pointer',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(139,69,19,0.14)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 16px rgba(139,69,19,0.08)'; }}
      >
        {p.coverPhoto ? (
          <img src={`${API_BASE}/uploads/${p.coverPhoto}`} alt={p.name}
            style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{
            width: '100%', height: 160,
            background: `linear-gradient(135deg, ${BRAND} 0%, #6B3410 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HardHat size={40} color="rgba(239,235,220,0.5)" />
          </div>
        )}
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>{p.name}</h3>
            <span style={{ flexShrink: 0, fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.text }}>
              {STATUS_LABEL[p.status] || p.status}
            </span>
          </div>
          {p.address && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: '0.82rem', marginBottom: 4 }}>
              <MapPin size={12} /><span>{p.address}</span>
            </div>
          )}
          {p.description && (
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {p.description}
            </p>
          )}
          {p.progress !== null && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>
                <span>Avancement</span>
                <span style={{ color: BRAND, fontWeight: 600 }}>{p.progress}%</span>
              </div>
              <div style={{ height: 5, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${p.progress}%`, background: `linear-gradient(90deg, ${BRAND}, #6B3410)`, borderRadius: 99 }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 12, color: BRAND, fontSize: '0.82rem', fontWeight: 600, gap: 4 }}>
            Voir le suivi <ChevronRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// Onglet actif porté par l'URL (/?onglet=chantiers) : le bouton « précédent »
// du navigateur et le lien « Retour aux chantiers » ramènent ainsi sur la
// liste des chantiers, et non sur l'accueil.
export const DNF_CHANTIERS_PATH = '/?onglet=chantiers';

function DnfLanding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('onglet') === 'chantiers' ? 'chantiers' : 'accueil';
  const setTab = (key) => setSearchParams(key === 'chantiers' ? { onglet: 'chantiers' } : {});
  const [chantiers, setChantiers] = useState(null); // null : pas encore chargés

  useEffect(() => {
    fetch(`${API_URL}/public/chantiers`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => setChantiers(Array.isArray(list) ? list : []))
      .catch(() => setChantiers([]));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  // En tête : le chantier mis en avant depuis l'admin (avec sa photo de
  // couverture), sinon le plus récent qui a une photo.
  const featured = chantiers && (chantiers.find(p => p.featured && p.coverPhoto) || chantiers.find(p => p.coverPhoto));
  const recent = chantiers ? chantiers.slice(0, 3) : [];

  return (
    <div className="dnf">
      {/* Barre de navigation */}
      <header className="dnf-top">
        <div className="dnf-container dnf-top-row">
          <button type="button" className="dnf-logo" onClick={() => setTab('accueil')} aria-label="DNF — accueil">
            <img src="/logo.png" alt="DNF" />
          </button>
          <nav className="dnf-nav" aria-label="Navigation principale">
            <button type="button" className={tab === 'accueil' ? 'is-active' : ''} onClick={() => setTab('accueil')}>Accueil</button>
            <button type="button" className={tab === 'chantiers' ? 'is-active' : ''} onClick={() => setTab('chantiers')}>Nos chantiers</button>
          </nav>
        </div>
      </header>

      {tab === 'accueil' && (
        <>
          {/* En-tête : promesse et dernier chantier en photo */}
          <section className="dnf-hero">
            <div className="dnf-container dnf-hero-grid">
              <div className="dnf-hero-copy">
                <div className="dnf-eyebrow">Ravalement · Pierre de taille · Peinture</div>
                <h1>Le savoir-faire des façades, de la pierre de taille à l'isolation.</h1>
                <p>
                  Entreprise qualifiée Qualibat basée à Chatou, DNF restaure et ravale les façades
                  d'immeubles et de maisons en Île-de-France : pierre de taille, enduits
                  traditionnels, isolation par l'extérieur, peinture.
                </p>
                <div className="dnf-actions">
                  <button type="button" className="dnf-btn" onClick={() => setTab('chantiers')}>
                    Voir nos chantiers <ChevronRight size={16} />
                  </button>
                </div>
                <div className="dnf-qualibat">
                  <img src="/qualibat.png" alt="Qualibat" />
                  <span>Entreprise qualifiée Qualibat</span>
                </div>
              </div>
              {featured && (
                <Link to={`/chantier/${featured.slug}`} className="dnf-hero-photo">
                  <img src={`${API_BASE}/uploads/${featured.coverPhoto}`} alt={featured.name} />
                  <div className="dnf-hero-photo-caption">
                    <span className="dnf-eyebrow">Chantier {STATUS_LABEL[featured.status] ? STATUS_LABEL[featured.status].toLowerCase() : ''}</span>
                    <strong>{featured.name}</strong>
                    {featured.address && <small>{featured.address}</small>}
                  </div>
                </Link>
              )}
            </div>
          </section>

          {/* Métiers, en liste éditoriale */}
          <section className="dnf-section">
            <div className="dnf-container dnf-split">
              <div className="dnf-split-head">
                <div className="dnf-eyebrow">Nos métiers</div>
                <h2>Quatre savoir-faire, un même soin du détail.</h2>
              </div>
              <ol className="dnf-services">
                {[
                  {
                    title: 'Pierre de taille',
                    desc: 'Nous réalisons des travaux de ravalement de façades anciennes en pierre et enduit traditionnel, dans le respect du patrimoine et conformément aux prescriptions des Architectes des bâtiments de France.',
                  },
                  {
                    title: 'Isolation par l\'extérieur',
                    desc: 'Nous réalisons les travaux d\'isolation par l\'extérieur, la solution de premier choix pour améliorer les performances énergétiques d\'un immeuble ou d\'une habitation. DNF, spécialiste de l\'isolation thermique par l\'extérieur, vous accompagne et réalise vos travaux.',
                  },
                  {
                    title: 'Ravalement',
                    desc: 'La société opère sur toutes les surfaces (pierre, brique, parement rapporté, béton, pans de bois, pans de fer, aluminium, murs-rideaux) et sur tous types de constructions : immeubles, bâtiments, maisons. Elle apporte un soin particulier à chaque étape du ravalement. Le travail préparatoire comprend décapage, hydrogommage, lavage, lavage haute pression, lessivage, décontamination, reprise de maçonnerie… Les traitements curatifs sont adaptés à tous les supports : façades en pierre, en béton, en brique et en plâtre. DNF propose également des travaux de ravalement intérieur : halls d\'immeubles, cages d\'escalier, bureaux.',
                  },
                  {
                    title: 'Travaux intérieurs',
                    desc: 'Dans la lignée des travaux extérieurs, les compétences des équipes sont sollicitées pour effectuer des travaux intérieurs : peinture, revêtements muraux et sols.',
                  },
                ].map((s, i) => (
                  <li key={s.title} className="dnf-service">
                    <span className="dnf-service-num">0{i + 1}</span>
                    <div>
                      <h3>{s.title}</h3>
                      <p>{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Derniers chantiers */}
          {recent.length > 0 && (
            <section className="dnf-section dnf-section-alt">
              <div className="dnf-container">
                <div className="dnf-row-head">
                  <div>
                    <div className="dnf-eyebrow">Suivi de chantier</div>
                    <h2>Nos derniers chantiers.</h2>
                  </div>
                  <button type="button" className="dnf-link" onClick={() => setTab('chantiers')}>
                    Tous les chantiers <ChevronRight size={15} />
                  </button>
                </div>
                <div className="dnf-grid">
                  {recent.map(p => <ChantierCard key={p.slug} p={p} />)}
                </div>
              </div>
            </section>
          )}

        </>
      )}

      {tab === 'chantiers' && (
        <section className="dnf-section dnf-chantiers">
          <div className="dnf-container">
            <div className="dnf-row-head">
              <div>
                <div className="dnf-eyebrow">Suivi de chantier</div>
                <h2>Nos chantiers.</h2>
              </div>
              <p className="dnf-row-note">Chaque chantier a sa page : avancement, photos et intervenants, mise à jour depuis le terrain.</p>
            </div>
            {chantiers === null ? (
              <p className="dnf-empty">Chargement...</p>
            ) : chantiers.length === 0 ? (
              <div className="dnf-empty">
                <HardHat size={40} />
                <p>Aucun chantier disponible pour le moment.</p>
              </div>
            ) : (
              <div className="dnf-grid">
                {chantiers.map(p => <ChantierCard key={p.slug} p={p} />)}
              </div>
            )}
          </div>
        </section>
      )}

      <footer className="dnf-footer">
        <div className="dnf-container dnf-footer-row">
          <span>© {new Date().getFullYear()} DNF SAS — Tous droits réservés</span>
          <span>42, rue des Cormiers · 78400 Chatou · <a href="tel:+33130715075">01 30 71 50 75</a></span>
        </div>
      </footer>
    </div>
  );
}


// Aiguillage de la racine selon le domaine :
// - domaine DNF (tenant historique) → vitrine DNF ;
// - domaine personnalisé d'un autre tenant → sa vitrine /e/{slug} ;
// - domaine technique (sans tenant) → aucune page publique : connexion.
export default function LandingPage() {
  const { tenant, tenantLoading, isLegacyDnfHost } = useTenant();

  if (tenantLoading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Chargement...</div>;
  }
  if (tenant && tenant.slug !== LEGACY_DNF_SLUG) {
    return <Navigate to={`/e/${tenant.slug}`} replace />;
  }
  if (tenant || isLegacyDnfHost) {
    return <DnfLanding />;
  }
  return <Navigate to="/login" replace />;
}
